import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Table } from "react-bootstrap";
import WagerModal from "../WagerModal";
import CashOutModal from "./CashOutModal";
import ActiveBetMatchExpand from "./ActiveBetMatchExpand";
import { getAllOrders } from "../../actions/gameSettings";
import { formatOdds } from "../../../utility/odds";
import { useSelector, useDispatch } from "react-redux";
import { momentFormat } from "../../lib/dateFormat";
import Loader from "../Loader";
import BetStatusBadge from "../../common/BetStatusBadge";
import {
  areAllExpandableExpanded,
  getExpandableTicketIds,
  hasAnyExpandedBoards,
  isActiveBetExpandable,
  toggleExpandedTicketIds,
} from "../../../utility/activeBetGameLookup";
import { formatMoney } from "../../../utility/cashoutCalculation";
import "./cashOutModal.css";
import "./openbetExpand.css";
import { OPEN_BETS_REFRESH_EVENT } from "../../../context/BetSlipProvider";

const REFRESH_MS = 60 * 1000;

const Openbettable = () => {
  const accountData = useSelector((state) => state.account);
  const [show, setShow] = useState(false);
  const [cashOutBet, setCashOutBet] = useState(null);
  const [showCashOut, setShowCashOut] = useState(false);
  const [allOrders, setAllOrders] = useState([]);
  const [orderItem, setOrderItem] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [expandedTicketIds, setExpandedTicketIds] = useState([]);
  const dispatch = useDispatch();
  const emailRef = useRef(accountData?.emailId);

  const expandableTicketIds = useMemo(
    () => getExpandableTicketIds(allOrders),
    [allOrders]
  );

  const allBoardsExpanded = useMemo(
    () => areAllExpandableExpanded(expandedTicketIds, allOrders),
    [expandedTicketIds, allOrders]
  );

  const anyBoardExpanded = hasAnyExpandedBoards(expandedTicketIds);

  useEffect(() => {
    emailRef.current = accountData?.emailId;
  }, [accountData?.emailId]);

  const handleClose = () => {
    setOrderItem({});
    setShow(false);
  };

  const handleShow = (data) => {
    setOrderItem(data);
    setShow(true);
  };

  const handleCashOutShow = (data) => {
    setCashOutBet(data);
    setShowCashOut(true);
  };

  const handleCashOutClose = () => {
    setCashOutBet(null);
    setShowCashOut(false);
  };

  const handleExpandToggle = (item) => {
    setExpandedTicketIds((current) =>
      toggleExpandedTicketIds(current, item?.ticketId)
    );
  };

  const handleExpandAllBoards = () => {
    setExpandedTicketIds(expandableTicketIds);
  };

  const handleCollapseAllBoards = () => {
    setExpandedTicketIds([]);
  };

  const handleExpandAllToggle = () => {
    if (allBoardsExpanded) {
      handleCollapseAllBoards();
    } else {
      handleExpandAllBoards();
    }
  };

  const isTicketExpanded = (ticketId) =>
    Boolean(ticketId) && expandedTicketIds.includes(ticketId);

  const renderLiveBoardButton = (item, { variant = "default" } = {}) => {
    if (!isActiveBetExpandable(item) || !item?.ticketId) return null;

    const isExpanded = isTicketExpanded(item.ticketId);
    const label = isExpanded ? "Hide Live Board" : "Live Board";

    return (
      <button
        type="button"
        className={[
          "openbet-live-board-btn",
          isExpanded ? "openbet-live-board-btn--active" : "",
          variant === "compact" ? "openbet-live-board-btn--compact" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={() => handleExpandToggle(item)}
        aria-expanded={isExpanded}
        aria-controls={`openbet-expand-${item.ticketId}`}
      >
        {label}
      </button>
    );
  };

  const fetchAllOrders = useCallback(async (opts = {}) => {
    const email = emailRef.current;
    if (!email) return;

    const silent = opts.silent === true;
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setFetchError(null);

    try {
      const { status, result, message } = await getAllOrders(
        { email },
        dispatch
      );
      if (status === true && Array.isArray(result)) {
        const sorted = [...result].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setAllOrders(sorted);
        setLastUpdated(new Date());
      } else if (!silent) {
        setAllOrders([]);
        setFetchError(message || "Unable to load active bets.");
      }
    } catch (err) {
      console.log("err", err);
      if (!silent) {
        setFetchError("Unable to load active bets. Please try again.");
      }
    } finally {
      if (!silent) setLoading(false);
      setRefreshing(false);
    }
  }, [dispatch]);

  useEffect(() => {
    const onOrdersRefresh = () => fetchAllOrders({ silent: true });
    window.addEventListener(OPEN_BETS_REFRESH_EVENT, onOrdersRefresh);
    return () =>
      window.removeEventListener(OPEN_BETS_REFRESH_EVENT, onOrdersRefresh);
  }, [fetchAllOrders]);

  useEffect(() => {
    fetchAllOrders();
    const id = setInterval(() => fetchAllOrders({ silent: true }), REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchAllOrders, accountData?.emailId]);

  const renderRiskWin = (item) => {
    const risk = Number(item.wagerAmount || item.totalLossamount || 0);
    const payout = Number(item.payOut || item.totalWinamount || 0);
    const win = Math.max(0, payout - risk);
    return (
      <p className="mb-0">
        ${risk.toFixed(2)} / ${win.toFixed(2)}
      </p>
    );
  };

  const openBetRowKey = (item, index) => {
    const tid = item?.ticketId;
    if (tid !== undefined && tid !== null && String(tid).length > 0) {
      return `openbet-${tid}-${item?.betType ?? "bet"}`;
    }
    return `openbet-fallback-${index}`;
  };

  const getUniqueSports = (bets) => {
    const seen = new Set();
    return bets
      ?.map((b) => b.sport_title || b.title)
      .filter((sport) => {
        if (seen.has(sport)) return false;
        seen.add(sport);
        return true;
      });
  };

  const renderDescription = (item) => {
    const { betType } = item;
    if (betType === "Straight") {
      return (
        <div className="openbet-desc-cell">
          <div className="openbet-desc-cell__head">
            <p className="text-primary fw-bold mb-0">Straight</p>
            {renderLiveBoardButton(item, { variant: "compact" })}
          </div>
          <div className="openbet-desc-cell__body">
            {item.homeTeam} vs {item.awayTeam}
            <br />
            {item.point ? `Line: ${item.point}` : ""}
            <br />
            Odds: {formatOdds(item.price)}
          </div>
        </div>
      );
    }
    if (betType === "Parlay") {
      return (
        <div className="openbet-desc-cell">
          <div className="openbet-desc-cell__head">
            <p className="text-primary fw-bold mb-0">Parlay</p>
            {renderLiveBoardButton(item, { variant: "compact" })}
          </div>
          <div className="openbet-desc-cell__body">
            {item.bettingData?.map((bet, i) => (
              <p key={i} className="mb-2">
                {bet.homeTeam} vs {bet.awayTeam}
                <br />
                {bet.point ? `Line: ${bet.point}` : ""}
                <br />
                Odds: {formatOdds(bet.price)}
              </p>
            ))}
          </div>
        </div>
      );
    }
    if (betType === "Teaser") {
      return (
        <div className="openbet-desc-cell">
          <div className="openbet-desc-cell__head">
            <p className="text-primary fw-bold mb-0">Teaser</p>
            {renderLiveBoardButton(item, { variant: "compact" })}
          </div>
          <div className="openbet-desc-cell__body">
            {item.bettingData?.map((bet, i) => (
              <p key={i} className="mb-2">
                {bet.homeTeam} vs {bet.awayTeam}
                <br />
                Orig: {bet.originalPoint || "-"} | Adj: {bet.point || "-"}
                <br />
                Odds: {formatOdds(bet.price)}
              </p>
            ))}
          </div>
        </div>
      );
    }
    return <p className="mb-0">{betType || "Bet"}</p>;
  };

  const renderCashOutAction = (item) => {
    if (item.betType !== "Straight" || !item.cashoutAvailable) return null;

    const offer = Number(item.cashoutOffer);
    const offerLabel = Number.isFinite(offer) && offer > 0 ? formatMoney(offer) : null;

    return (
      <div className="openbet-cashout">
        <button
          type="button"
          className="btn img_btn getbtn openbet-cashout__btn"
          onClick={() => handleCashOutShow(item)}
          aria-label={
            offerLabel
              ? `Cash out ticket ${item.ticketId} for $${offerLabel}`
              : `Cash out ticket ${item.ticketId}`
          }
        >
          Cash Out
        </button>
        {offerLabel ? (
          <div
            className="openbet-cashout-amount"
            role="status"
            aria-label={`Amount $${offerLabel}`}
          >
            <span className="openbet-cashout-amount__label">Amount</span>
            <span className="openbet-cashout-amount__value">${offerLabel}</span>
          </div>
        ) : null}
      </div>
    );
  };

  const renderSportCell = (item) => {
    if (item.betType === "Straight") {
      return <p className="mb-0">{item.sport_title || item.title}</p>;
    }
    const uniqueSports = getUniqueSports(item.bettingData);
    if (item.betType === "Teaser") {
      return <p className="mb-0">{uniqueSports?.join(", ")}</p>;
    }
    return uniqueSports?.map((sport, i) => (
      <p key={i} className="mb-0">
        {sport}
      </p>
    ));
  };

  const renderBetRow = (item, index) => {
    if (!["Straight", "Parlay", "Teaser"].includes(item.betType)) {
      return null;
    }

    const canExpand = isActiveBetExpandable(item);
    const isExpanded = canExpand && isTicketExpanded(item.ticketId);

    return (
      <tbody key={openBetRowKey(item, index)}>
        <tr className="match_collections">
          <td>
            <Button
              onClick={() => handleShow(item)}
              className="open_wager_ticket_btn"
            >
              #{item.ticketId}
            </Button>
          </td>
          <td>
            <p className="mb-0">{momentFormat(item.createdAt)}</p>
          </td>
          <td>{renderSportCell(item)}</td>
          <td>{renderDescription(item)}</td>
          <td>
            <BetStatusBadge item={item} showDetail={true} />
          </td>
          <td>{renderRiskWin(item)}</td>
          <td>
            <div className="openbet-actions">
              {renderLiveBoardButton(item)}
              {renderCashOutAction(item)}
              <button
                type="button"
                className="btn img_btn getbtn"
                onClick={() => handleShow(item)}
              >
                View
              </button>
            </div>
          </td>
        </tr>
        {isExpanded && (
          <tr className="openbet-expand-row" id={`openbet-expand-${item.ticketId}`}>
            <td colSpan={7}>
              <ActiveBetMatchExpand bet={item} />
            </td>
          </tr>
        )}
      </tbody>
    );
  };

  return (
    <section>
      <div className="dashboard_content_sec">
        <div className="teams_table_sec openbet">
          <div className="openbet-page-header mb-3">
            <div className="match_heading mb-0 openbet-page-header__title">
              ACTIVE BETS
            </div>
            <div className="openbet-page-header__actions">
              {lastUpdated ? (
                <span className="small text-muted openbet-page-header__updated">
                  Updated {momentFormat(lastUpdated)}
                  {refreshing ? " · refreshing…" : ""}
                </span>
              ) : null}
              {expandableTicketIds.length > 0 ? (
                <button
                  type="button"
                  className="img_btn py-1 px-2 openbet-page-header__expand-all"
                  disabled={loading || refreshing}
                  onClick={handleExpandAllToggle}
                  aria-pressed={allBoardsExpanded}
                >
                  {allBoardsExpanded ? "Collapse All" : "Expand All"}
                </button>
              ) : null}
              {anyBoardExpanded && !allBoardsExpanded ? (
                <button
                  type="button"
                  className="img_btn py-1 px-2 openbet-page-header__collapse-all"
                  disabled={loading || refreshing}
                  onClick={handleCollapseAllBoards}
                >
                  Collapse All
                </button>
              ) : null}
              <button
                type="button"
                className="img_btn img_btn_xxl py-1 px-3 openbet-page-header__refresh"
                disabled={loading || refreshing}
                onClick={() => fetchAllOrders({ silent: true })}
              >
                Refresh
              </button>
            </div>
          </div>

          {loading && (
            <div className="py-5 d-flex justify-content-center">
              <Loader />
            </div>
          )}

          {!loading && fetchError && (
            <p className="text-danger text-center py-4 mb-0">{fetchError}</p>
          )}

          {!loading && !fetchError && (
            <Table responsive>
              {allOrders.length > 0 && (
                <thead>
                  <tr>
                    <th>TICKET NO</th>
                    <th>DATE PLACED</th>
                    <th className="sportTh">SPORT</th>
                    <th className="descTh">DESCRIPTION</th>
                    <th>STATUS</th>
                    <th>RISK / WIN ($)</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
              )}

              {allOrders.length > 0 ? (
                allOrders.map((item, index) => renderBetRow(item, index))
              ) : (
                <tbody>
                  <tr>
                    <td colSpan={7} className="text-center">
                      NO ACTIVE BETS
                    </td>
                  </tr>
                </tbody>
              )}
            </Table>
          )}
        </div>
      </div>

      <WagerModal show={show} handleClose={handleClose} item={orderItem} />
      <CashOutModal
        show={showCashOut}
        onClose={handleCashOutClose}
        bet={cashOutBet}
        email={accountData?.emailId}
        onSuccess={() => fetchAllOrders({ silent: true })}
      />
    </section>
  );
};

export default Openbettable;
